import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import 'echarts-gl'; // Не забудьте установить и импортировать echarts-gl для 3D
import ReactECharts from 'echarts-for-react';
import { Scatter3DDataItem } from '../../types';
import { useAppSelector } from '../../redux/hooks';
import { selectPropertyTypes } from '../../redux/api/documents/documentsSelectors';
import { notification } from 'antd';

interface Scatter3DProps {
    data: Scatter3DDataItem[];
}

const Scatter3DWithECharts: React.FC<Scatter3DProps> = ({ data }) => {
    const chartRef = useRef<ReactECharts>(null);

    // 1. Группировка точек по propertyTypeId
    const groupedData = useMemo(() => {
        const groups: Record<string, Scatter3DDataItem[]> = {};
        data.forEach((item) => {
            const key = item.propertyTypeId || 'unknown';
            if (!groups[key]) groups[key] = [];
            groups[key].push(item);
        });
        return groups;
    }, [data]);

    // 2. Список уникальных propertyTypeId (отсортированный для стабильности)
    const sortedTypeIds = useMemo(() => Object.keys(groupedData).sort(), [groupedData]);

    // 3. Получаем список уникальных documentId и назначаем им символы
    const documents = useMemo(() => {
        const setDocs = new Set<string>();
        data.forEach((item) => setDocs.add(item.documentId));
        return Array.from(setDocs);
    }, [data]);
    const symbols = ['circle', 'square', 'diamond', 'cross', 'triangle-up', 'star', 'pentagon'];
    const symbolMap = useMemo(() => {
        const map: Record<string, string> = {};
        documents.forEach((docId, idx) => {
            map[docId] = symbols[idx % symbols.length];
        });
        return map;
    }, [documents]);

    // 4. Генерируем цвета для каждой группы (равномерное распределение hues)
    const typeColorMap = useMemo(() => {
        const total = sortedTypeIds.length;
        const map: Record<string, string> = {};
        sortedTypeIds.forEach((tid, idx) => {
            const hue = Math.round((360 / total) * idx);
            map[tid] = `hsl(${hue}, 70%, 50%)`;
        });
        return map;
    }, [sortedTypeIds]);

    // 5. Получаем типы из Redux и формируем мапу id → имя
    const propertyTypes = useAppSelector(selectPropertyTypes);
    const propertyTypeMap = useMemo(() => {
        const nameCounts = new Map<string, number>();
        propertyTypes.forEach((pt) => {
            const count = nameCounts.get(pt.name) || 0;
            nameCounts.set(pt.name, count + 1);
        });
        const map: Record<string, string> = {};
        propertyTypes.forEach((pt) => {
            if ((nameCounts.get(pt.name) || 0) > 1) {
                map[pt.id] = `${pt.name} (${pt.id.slice(0, 4)})`;
            } else {
                map[pt.id] = pt.name;
            }
        });
        return map;
    }, [propertyTypes]);

    // 6. Формируем массив серий для графика: каждая серия соответствует группе
    const series = useMemo(() => {
        return sortedTypeIds.map((tid) => {
            const items = groupedData[tid];
            const groupName = propertyTypeMap[tid] || tid;
            const dataArr = items.map((it) => ({
                value: [it.x, it.y, it.z],
                name: it.text, // Используется для tooltip и копирования
                symbol: symbolMap[it.documentId],
            }));
            return {
                name: groupName,
                type: 'scatter3D',
                data: dataArr,
                itemStyle: {
                    color: typeColorMap[tid],
                    opacity: 0.8,
                },
                symbolSize: 12,
            };
        });
    }, [sortedTypeIds, groupedData, symbolMap, typeColorMap, propertyTypeMap]);

    useEffect(() => {
        // Если данные есть, после открытия модального окна можно вызвать resize
        const timeoutId = setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            chartRef.current?.getEchartsInstance().resize();
        }, 300);
        return () => clearTimeout(timeoutId);
    }, []);


    // 7. Конфигурация графика
    const option = useMemo(() => {
        return {
            legend: {
                show: true,
                left: 'left',
            },
            triggerOn: 'click',
            tooltip: {
                formatter: (params: any) => {
                    if (!params.data) return '';
                    const v = params.data.value;
                    return `
            <div><strong>${params.seriesName}</strong></div>
            <div>(${v[0]}, ${v[1]}, ${v[2]})</div>
            <div>${params.data.name || ''}</div>
          `;
                }
            },
            animationDurationUpdate: 300,
            xAxis3D: { name: 'X', type: 'value' },
            yAxis3D: { name: 'Y', type: 'value' },
            zAxis3D: { name: 'Z', type: 'value' },
            grid3D: {
                viewControl: {
                    alpha: 30,
                    beta: 160,
                }
            },
            series: series
        };
    }, [series]);

    // 8. Обработчик клика по точке для копирования текста.
    // По нажатию левой кнопкой (click) копируется текст в формате:
    // "{propertyTypeName}: {propertyText}"
    const onChartClick = useCallback((params: any, _chart: any) => {
        if (params.data) {
            const seriesName = params.seriesName;
            const propertyText = params.data.name || '';
            const copyText = `${seriesName}: ${propertyText}`;

            navigator.clipboard.writeText(copyText)
                .then(() => {
                    notification.success({
                        message: 'Скопировано',
                        description: `Текст "${copyText}" скопирован в буфер обмена.`,
                        placement: 'topRight',
                    });
                })
                .catch((err) => {
                    notification.error({
                        message: 'Ошибка копирования',
                        description: 'Не удалось скопировать текст.',
                        placement: 'topRight',
                    });
                    console.error('Ошибка копирования:', err);
                });
        }
    }, []);

    // 9. Сохраняем объект событий в state (как в примере)
    const [onEventsState, setOnEventsState] = useState({ click: onChartClick });
    useEffect(() => {
        setOnEventsState({ click: onChartClick });
    }, [onChartClick]);

    return (
        <div style={{
                display: 'flex',
                flexDirection: 'column',
                // например, делаем высоту 100% у родителя,
                // но только если и у всего дерева родителей тоже есть 100% высоты.
                width: '100%',
                height: '100%',
                minHeight: 0
        }}>
            <ReactECharts
                option={option}
                style={{ height: '100%', width: '100%' }}
                ref={chartRef}
                onEvents={onEventsState}
            />
        </div>
    );
};

export default React.memo(Scatter3DWithECharts);
